import * as Yup from 'yup';

import {
  Button,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
} from 'react-bootstrap';
import { Field, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import {
  bedroomOptions,
  electricityHardLimit,
  electricitySoftLimit,
  energyUnits,
  getEnergyDefault,
  getHeatingMethodName,
  getHeatingUnitOfMeasure,
  kwhFuelHardLimit,
  kwhFuelSoftLimit,
  litreFuelHardLimit,
  litreFuelSoftLimit,
} from '../../utils/netZeroHelper';
import { contentfulPropTypes, netZeroPropTypes } from '../../propTypes';

import ErrorLabel from '../../ui/ErrorLabel';
import { LoginCard } from '../../ui';
import NetZeroEnergyWarningModal from '../../ui/Modal/NetZeroEnergyWarningModal';
import { RadioWrapper } from '../../ui/Styled';
import ReactMarkdown from 'react-markdown';
import cx from 'classnames';
import { primary } from '../../theme/colors';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import withContentfulTag from '../../utils/withContentfulTag';

const StyledContainer = styled(Container)`
  .row {
    .form-check {
      label {
        width: 100%;
        white-space: nowrap;

        @media screen and (max-width: 575px) {
          padding-left: 32px;
        }
      }
    }

    .selected {
      margin-bottom: 0;
      padding-bottom: 48px;
      border-bottom: none;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      background: #fff;

      @media screen and (max-width: 575px) {
        padding-bottom: 24px;
      }
    }

    .selection {
      border: 2px solid ${primary};
      border-radius: 4px;
      position: relative;
      top: -2px;
    }

    .form-control {
      border-right: none;
    }

    .input-group-text {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      background: #fff;
      border-color: #afb6b9;
      border-left: none;
      padding-left: 0;

      .kwh-wrapper {
        border-left: 1px solid #afb6b9;
        padding-left: 0.55rem;
      }
    }
  }
`;

const StyledDiv = styled.div`
  border-bottom: solid 1px #afb6b9;
  margin-bottom: 22px;
`;

const StyledCol = styled(Col)`
  z-index: 5;
`;

const EnergyContainer = ({
  contentful,
  loading,
  fetched,
  usage,
  setUsage,
  heatingMethod,
}) => {
  const navigate = useNavigate();
  const [schema, setSchema] = useState(undefined);
  const [showWarning, setShowWarning] = useState(false);
  const [bypassWarning, setBypassWarning] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => {
    const electricityMessage =
      'Please enter your annual electricity usage in kWh';
    const heatingUnitOfMeasure = getHeatingUnitOfMeasure(heatingMethod);
    const heatingMessage = `Please enter your annual ${getHeatingMethodName(
      heatingMethod,
    ).toLowerCase()} usage in ${heatingUnitOfMeasure}`;
    const heatingHardLimit =
      heatingUnitOfMeasure === energyUnits.KWH
        ? kwhFuelHardLimit
        : litreFuelHardLimit;
    setSchema(
      Yup.object().shape({
        bedroomCount: Yup.mixed()
          .oneOf([
            bedroomOptions.ONE,
            bedroomOptions.TWO_OR_THREE,
            bedroomOptions.FOUR_PLUS,
          ])
          .required(),
        electricityUsage: Yup.number()
          .min(0, electricityMessage)
          .max(
            electricityHardLimit,
            'The value you’ve entered is very high compared to the average home',
          )
          .required(electricityMessage),
        heatingUsage: Yup.number()
          .min(0, heatingMessage)
          .max(
            heatingHardLimit,
            'The value you’ve entered is very high compared to the average home',
          )
          .required(heatingMessage),
      }),
    );
  }, [heatingMethod]);

  const shouldShowHeatingInput = heatingMethod => {
    if (
      heatingMethod === 'heat-pump' ||
      heatingMethod === 'electric-radiators'
    ) {
      return false;
    }
    return true;
  };

  const onSubmitEnergyUsage = ({
    bedroomCount,
    electricityUsage,
    heatingUsage,
  }) => {
    electricityUsage = Math.round(electricityUsage);
    heatingUsage = Math.round(heatingUsage);

    const unitOfMeasure = getHeatingUnitOfMeasure(heatingMethod);
    setUsage({
      bedroomCount,
      electricityUsage,
      heatingUsage: shouldShowHeatingInput(heatingMethod) ? heatingUsage : 0,
    });
    if (
      !bypassWarning &&
      (electricityUsage >= electricitySoftLimit ||
        (heatingUsage >= kwhFuelSoftLimit &&
          unitOfMeasure === energyUnits.KWH) ||
        (heatingUsage >= litreFuelSoftLimit &&
          unitOfMeasure === energyUnits.LITRES))
    ) {
      setShowWarning(true);
    } else {
      navigate('/net-zero/car');
    }
  };

  return (
    !loading &&
    fetched && (
      <LoginCard onBack={() => navigate('/net-zero/heating')}>
        <p>
          Step <strong>2</strong> of <strong>4</strong>
        </p>
        <h1 className="pb-3">{contentful.title}</h1>
        <ReactMarkdown>{contentful?.body}</ReactMarkdown>
        <p className="text-left">
          <strong>How many bedrooms do you have?</strong>
        </p>
        {schema && (
          <Formik
            onSubmit={onSubmitEnergyUsage}
            initialValues={usage}
            validationSchema={schema}
            validateOnChange>
            {({
              handleSubmit,
              isSubmitting,
              values,
              setFieldValue,
              resetForm,
            }) => (
              <Form onSubmit={handleSubmit} noValidate>
                <StyledContainer>
                  <Row>
                    <StyledCol xs={4} sm={4} className="pl-0 pr-sm-2 pr-1">
                      <Field name="bedroomCount">
                        {({ field, meta }) => (
                          <RadioWrapper
                            className={cx({
                              selected: field.value === bedroomOptions.ONE,
                            })}>
                            <Form.Check
                              type="radio"
                              name={field.name}
                              id={bedroomOptions.ONE}
                              value={bedroomOptions.ONE}
                              label="1"
                              onChange={e => {
                                setFieldValue(
                                  'electricityUsage',
                                  getEnergyDefault(
                                    bedroomOptions.ONE,
                                    heatingMethod,
                                    true,
                                  ),
                                  true,
                                );
                                setFieldValue(
                                  'heatingUsage',
                                  getEnergyDefault(
                                    bedroomOptions.ONE,
                                    heatingMethod,
                                  ),
                                  true,
                                );
                                field.onChange(e);
                              }}
                              onBlur={field.onBlur}
                              className={cx({ error: !!meta.error })}
                            />
                          </RadioWrapper>
                        )}
                      </Field>
                    </StyledCol>
                    <StyledCol xs={4} sm={4} className="px-sm-1 px-0">
                      <Field name="bedroomCount">
                        {({ field, meta }) => (
                          <RadioWrapper
                            className={cx({
                              selected:
                                field.value === bedroomOptions.TWO_OR_THREE,
                            })}>
                            <Form.Check
                              type="radio"
                              name={field.name}
                              id={bedroomOptions.TWO_OR_THREE}
                              value={bedroomOptions.TWO_OR_THREE}
                              label={
                                <>
                                  <span className="d-inline d-sm-none">
                                    2-3
                                  </span>
                                  <span className="d-none d-sm-inline">
                                    2 to 3
                                  </span>
                                </>
                              }
                              onChange={e => {
                                setFieldValue(
                                  'electricityUsage',
                                  getEnergyDefault(
                                    bedroomOptions.TWO_OR_THREE,
                                    heatingMethod,
                                    true,
                                  ),
                                  true,
                                );
                                setFieldValue(
                                  'heatingUsage',
                                  getEnergyDefault(
                                    bedroomOptions.TWO_OR_THREE,
                                    heatingMethod,
                                  ),
                                  true,
                                );
                                field.onChange(e);
                              }}
                              onBlur={field.onBlur}
                              className={cx({ error: !!meta.error })}
                            />
                          </RadioWrapper>
                        )}
                      </Field>
                    </StyledCol>
                    <StyledCol xs={4} sm={4} className="pl-sm-2 pr-0 pl-1">
                      <Field name="bedroomCount">
                        {({ field, meta }) => (
                          <RadioWrapper
                            className={cx({
                              selected:
                                field.value === bedroomOptions.FOUR_PLUS,
                            })}>
                            <Form.Check
                              type="radio"
                              name={field.name}
                              id={bedroomOptions.FOUR_PLUS}
                              value={bedroomOptions.FOUR_PLUS}
                              label="4+"
                              onChange={e => {
                                setFieldValue(
                                  'electricityUsage',
                                  getEnergyDefault(
                                    bedroomOptions.FOUR_PLUS,
                                    heatingMethod,
                                    true,
                                  ),
                                  true,
                                );
                                setFieldValue(
                                  'heatingUsage',
                                  getEnergyDefault(
                                    bedroomOptions.FOUR_PLUS,
                                    heatingMethod,
                                  ),
                                  true,
                                );
                                field.onChange(e);
                              }}
                              onBlur={field.onBlur}
                              className={cx({ error: !!meta.error })}
                            />
                          </RadioWrapper>
                        )}
                      </Field>
                    </StyledCol>
                  </Row>
                  <Row>
                    <Col
                      sm={12}
                      className={`px-0 py-3 ${cx({
                        selection: !!values.bedroomCount,
                      })}`}>
                      {!!values.bedroomCount ? (
                        <div className="px-3">
                          <p>
                            We&apos;ve filled in average values below, but you
                            can change them to be personalised to you.
                          </p>
                        </div>
                      ) : (
                        <StyledDiv></StyledDiv>
                      )}
                      <div className="px-3">
                        <Field name="electricityUsage">
                          {({ field, meta }) => (
                            <Form.Group
                              controlId="electricityUsage"
                              className="text-left">
                              <Form.Label>Annual electricity usage</Form.Label>
                              <InputGroup>
                                <Form.Control
                                  type="number"
                                  value={field.value}
                                  step="100"
                                  onChange={field.onChange}
                                  onBlur={field.onBlur}
                                  isValid={!meta.error && !!field.value}
                                  placeholder="Electricity usage"
                                  className={cx({
                                    error: !!meta.touched && !!meta.error,
                                  })}
                                />
                                <InputGroup.Text>
                                  <div className="kwh-wrapper">kWh</div>
                                </InputGroup.Text>
                              </InputGroup>
                              {meta.error && meta.touched && (
                                <ErrorLabel label={meta.error} />
                              )}
                            </Form.Group>
                          )}
                        </Field>
                        {shouldShowHeatingInput(heatingMethod) && (
                          <Field name="heatingUsage">
                            {({ field, meta }) => (
                              <Form.Group
                                controlId="heatingUsage"
                                className="text-left">
                                <Form.Label>
                                  Annual{' '}
                                  {getHeatingMethodName(
                                    heatingMethod,
                                  ).toLowerCase()}{' '}
                                  usage
                                </Form.Label>
                                <InputGroup>
                                  <Form.Control
                                    type="number"
                                    value={field.value}
                                    onChange={field.onChange}
                                    step="100"
                                    onBlur={field.onBlur}
                                    isValid={!meta.error && !!field.value}
                                    placeholder={`${getHeatingMethodName(
                                      heatingMethod,
                                    )} usage`}
                                    className={cx({
                                      error: !!meta.touched && !!meta.error,
                                    })}
                                  />
                                  <InputGroup.Text>
                                    <div className="kwh-wrapper">
                                      {getHeatingUnitOfMeasure(heatingMethod)}
                                    </div>
                                  </InputGroup.Text>
                                </InputGroup>
                                {meta.error && meta.touched && (
                                  <ErrorLabel label={meta.error} />
                                )}
                              </Form.Group>
                            )}
                          </Field>
                        )}
                        <ReactMarkdown>{contentful?.bodyEnd}</ReactMarkdown>
                      </div>
                    </Col>
                  </Row>
                </StyledContainer>
                <Button
                  className="w-100"
                  type="submit"
                  variant="primary"
                  disabled={!values.bedroomCount}
                  ref={buttonRef}>
                  {!isSubmitting ? (
                    contentful.primaryButtonText
                  ) : (
                    <Spinner animation="border" size="sm" />
                  )}
                </Button>
                <NetZeroEnergyWarningModal
                  show={showWarning}
                  onClose={() => {
                    resetForm();
                    setShowWarning(false);
                  }}
                  onContinue={() => {
                    setBypassWarning(true);
                    buttonRef.current.click();
                  }}
                />
              </Form>
            )}
          </Formik>
        )}
      </LoginCard>
    )
  );
};

EnergyContainer.propTypes = {
  ...contentfulPropTypes,
  ...netZeroPropTypes,
};

export default withContentfulTag(
  EnergyContainer,
  'web_app_netzero_energy_page',
);
